import { useMemo, useState } from 'react';
import {
  RiCompass3Line,
  RiMapPinLine,
  RiRouteLine,
  RiGroupLine,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useCanvasStore } from '../canvas/canvasStore';
import { useStore } from '../state/store';
import { labelForType } from './icons';
import {
  clusterPoints,
  geoBounds,
  geoPoints,
  graticule,
  haversineKm,
  projectPoint,
} from '../analysis/geo';
import { cn } from '@/lib/utils';

const SIZE = 300;

const TYPE_COLOR: Record<string, string> = {
  location: '#0f766e',
  person: '#1e3a5f',
  organization: '#6d28d9',
  ip: '#b3261e',
  domain: '#0f7a5a',
  social: '#c2410c',
};

export function GeoPanel() {
  const entities = useStore(state => state.entities);
  const toast = useStore(state => state.toast);
  const controller = useCanvasStore(state => state.controller);
  const [threshold, setThreshold] = useState(25);
  const [activeCluster, setActiveCluster] = useState<string | null>(null);

  const points = useMemo(() => geoPoints(entities), [entities]);
  const bounds = useMemo(() => geoBounds(points), [points]);
  const clusters = useMemo(() => clusterPoints(points, threshold), [points, threshold]);
  const grid = useMemo(() => (bounds ? graticule(bounds, 4) : null), [bounds]);

  const missingGeo = entities.length - points.length;

  if (points.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <span className="label-caps">地理分布</span>
        <div className="rounded-[var(--radius)] border border-dashed border-border p-5 text-center text-[11.5px] leading-relaxed text-muted-foreground">
          暂无带坐标的线索。
          <br />
          在「线索」登记时可填经纬度（`geo.lat` / `geo.lng`），采集到的位置线索也可补录。
        </div>
      </div>
    );
  }

  const drawGeoRopes = () => {
    if (!controller) return;
    const wallByRef = new Map<string, string>();
    for (const element of controller.surface.all()) {
      if (element.refKind === 'entity' && element.refId) wallByRef.set(element.refId, element.id);
    }
    let drawn = 0;
    controller.pushHistory();
    for (const cluster of clusters) {
      if (cluster.points.length < 2) continue;
      const ids = cluster.points.map(p => wallByRef.get(p.id)).filter(Boolean) as string[];
      for (let i = 0; i < ids.length - 1; i++) {
        const exists = controller.surface.all().some(el => {
          if (el.type !== 'rope') return false;
          const props = el.props as { source?: { id?: string }; target?: { id?: string } };
          return (
            (props.source?.id === ids[i] && props.target?.id === ids[i + 1]) ||
            (props.source?.id === ids[i + 1] && props.target?.id === ids[i])
          );
        });
        if (exists) continue;
        controller.addRope({ id: ids[i], anchor: [0.5, 0] }, { id: ids[i + 1], anchor: [0.5, 0] }, '同地共现');
        drawn += 1;
      }
    }
    controller.requestRender();
    useCanvasStore.getState().bump();
    toast(drawn ? 'success' : 'info', drawn ? `已拉出 ${drawn} 条同地红线` : '同地关系已存在或节点未上墙');
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-center gap-1.5">
        <RiCompass3Line className="size-3.5 text-[var(--color-evidence)]" />
        <span className="text-[12px] font-semibold">地理分布</span>
        <Badge variant="muted" className="ml-auto">
          {points.length} 个坐标点
        </Badge>
      </div>

      {/* 坐标平面（离线经纬网，不依赖在线底图） */}
      <div className="rounded-[var(--radius)] border border-border bg-card p-2">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="mx-auto block w-full max-w-[300px]">
          <rect x={0} y={0} width={SIZE} height={SIZE} fill="#fbfbfa" rx={6} />
          {grid &&
            bounds &&
            grid.lat.map((lat, i) => {
              const y = projectPoint({ lat, lng: bounds.minLng }, bounds).y * SIZE;
              return (
                <g key={`lat-${i}`}>
                  <line x1={0} y1={y} x2={SIZE} y2={y} stroke="rgba(30,58,95,0.10)" strokeWidth={1} />
                  <text x={3} y={y - 2} fontSize={7} fill="rgba(30,58,95,0.45)">
                    {lat.toFixed(1)}°
                  </text>
                </g>
              );
            })}
          {grid &&
            bounds &&
            grid.lng.map((lng, i) => {
              const x = projectPoint({ lat: bounds.minLat, lng }, bounds).x * SIZE;
              return (
                <g key={`lng-${i}`}>
                  <line x1={x} y1={0} x2={x} y2={SIZE} stroke="rgba(30,58,95,0.10)" strokeWidth={1} />
                  <text x={x + 2} y={SIZE - 3} fontSize={7} fill="rgba(30,58,95,0.45)">
                    {lng.toFixed(0)}°
                  </text>
                </g>
              );
            })}

          {/* 同地聚类连线 */}
          {clusters.map(cluster => {
            if (cluster.points.length < 2) return null;
            const isActive = activeCluster === cluster.key;
            return (
              <g key={`c-${cluster.key}`}>
                {cluster.points.slice(0, -1).map((p, i) => {
                  const a = projectPoint(p, bounds!);
                  const b = projectPoint(cluster.points[i + 1], bounds!);
                  return (
                    <line
                      key={`${cluster.key}-${i}`}
                      x1={a.x * SIZE}
                      y1={a.y * SIZE}
                      x2={b.x * SIZE}
                      y2={b.y * SIZE}
                      stroke={isActive ? 'rgba(179,38,30,0.75)' : 'rgba(179,38,30,0.28)'}
                      strokeWidth={isActive ? 1.6 : 1}
                      strokeDasharray="3 2"
                    />
                  );
                })}
              </g>
            );
          })}

          {/* 点 */}
          {points.map(point => {
            const { x, y } = projectPoint(point, bounds!);
            const inActive = activeCluster
              ? clusters.find(c => c.key === activeCluster)?.points.some(p => p.id === point.id)
              : false;
            const r = 3 + point.confidence * 3;
            return (
              <g key={point.id}>
                <circle
                  cx={x * SIZE}
                  cy={y * SIZE}
                  r={r}
                  fill={TYPE_COLOR[point.type] ?? '#6d7176'}
                  fillOpacity={activeCluster && !inActive ? 0.25 : 0.9}
                  stroke="#ffffff"
                  strokeWidth={1}
                />
                <title>
                  {point.label} · {point.place} · 置信度 {(point.confidence * 100).toFixed(0)}%
                </title>
              </g>
            );
          })}
        </svg>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2 text-[9.5px] text-muted-foreground">
          <span>离线经纬网投影（等距圆柱），点大小=置信度</span>
        </div>
      </div>

      {/* 阈值与操作 */}
      <div className="flex items-center gap-2">
        <span className="text-[10.5px] text-muted-foreground">同地阈值</span>
        <input
          type="range"
          min={5}
          max={200}
          step={5}
          value={threshold}
          onChange={e => setThreshold(Number(e.target.value))}
          className="h-1 flex-1 accent-[var(--color-primary)]"
        />
        <span className="w-12 text-right font-mono text-[10.5px]">{threshold} km</span>
      </div>
      <Button size="xs" variant="outline" onClick={drawGeoRopes}>
        <RiRouteLine className="size-3" />
        同地关系全部上墙
      </Button>

      {/* 聚类列表 */}
      <div className="flex flex-col gap-1.5">
        <span className="label-caps">同地共现（{clusters.length} 组）</span>
        {clusters.map(cluster => (
          <button
            key={cluster.key}
            type="button"
            onMouseEnter={() => setActiveCluster(cluster.key)}
            onMouseLeave={() => setActiveCluster(null)}
            className={cn(
              'flex items-start gap-2 rounded-[var(--radius-sm)] border bg-card p-2 text-left transition-colors',
              activeCluster === cluster.key ? 'border-[var(--color-cord)]/50' : 'border-border'
            )}
          >
            <RiMapPinLine className="mt-0.5 size-3.5 shrink-0 text-[var(--color-evidence)]" />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <span className="truncate text-[11.5px] font-medium">{cluster.place}</span>
                <Badge variant="muted" className="shrink-0">
                  {cluster.points.length} 点
                </Badge>
                {cluster.radiusKm > 0 && (
                  <span className="shrink-0 font-mono text-[9.5px] text-muted-foreground">
                    跨 {cluster.radiusKm.toFixed(0)} km
                  </span>
                )}
              </span>
              <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">
                {cluster.points
                  .slice(0, 4)
                  .map(p => `${labelForType(p.type)}：${p.label}`)
                  .join(' · ')}
                {cluster.points.length > 4 ? ` 等 ${cluster.points.length} 项` : ''}
              </span>
            </span>
          </button>
        ))}
      </div>

      {points.length > 1 && (
        <div className="flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-border bg-muted/40 p-2 text-[10.5px] text-muted-foreground">
          <RiGroupLine className="size-3 shrink-0" />
          最远两点相距
          <span className="font-mono">
            {Math.max(
              ...points.flatMap((a, i) => points.slice(i + 1).map(b => haversineKm(a, b)))
            ).toFixed(0)}{' '}
            km
          </span>
        </div>
      )}

      {missingGeo > 0 && (
        <p className="text-[10px] text-muted-foreground">
          另有 {missingGeo} 条线索未填坐标，未参与地理分析。
        </p>
      )}
    </div>
  );
}